'use client'

/**
 * PhosphorIcon Component
 *
 * Handles rendering of Phosphor icons.
 *
 * For component documentation, icon management, and optimization guidelines,
 * please refer to the README.md file in this directory.
 */

import {
  ArrowElbowDownRight,
  ArrowLineDown,
  ArrowLineLeft,
  ArrowLineRight,
  ArrowLineUp,
  ArrowsSplitIcon,
  ChartLine,
  ChartPieSlice,
  ChartScatter,
  CircleHalf,
  ClockCounterClockwise,
  Copy,
  Crosshair,
  Equals,
  Eye,
  EyeSlash,
  FileCsv,
  FileXls,
  Flag,
  FunnelSimpleIcon,
  GitCommit,
  GlobeHemisphereWest,
  GreaterThan,
  GreaterThanOrEqual,
  LessThan,
  LessThanOrEqual,
  MemberOf,
  NotEquals,
  Note,
  NotMemberOf,
  PlusMinus,
  Prohibit,
  PushPinSimpleIcon,
  SealPercent,
  SidebarSimple,
  Target,
  TextAUnderline,
} from '@phosphor-icons/react'

import { IconBase, type IconBaseProps } from './base'
import { PhosphorIconName, type PhosphorWeight } from './types'

interface PhosphorIconProps extends IconBaseProps {
  weight?: PhosphorWeight
}

// Create a mapping between enum values and actual icon components
const iconMap: Partial<
  Record<PhosphorIconName, React.ComponentType<React.SVGProps<SVGSVGElement>>>
> = {
  [PhosphorIconName.ArrowLineLeftIcon]: ArrowLineLeft,
  [PhosphorIconName.ArrowLineRightIcon]: ArrowLineRight,
  [PhosphorIconName.ArrowLineUpIcon]: ArrowLineUp,
  [PhosphorIconName.ArrowLineDownIcon]: ArrowLineDown,
  [PhosphorIconName.ArrowsSplitIcon]: ArrowsSplitIcon,
  [PhosphorIconName.ArrowElbowDownRightIcon]: ArrowElbowDownRight,
  [PhosphorIconName.ChartLineIcon]: ChartLine,
  [PhosphorIconName.ChartPieSliceIcon]: ChartPieSlice,
  [PhosphorIconName.ChartScatterIcon]: ChartScatter,
  [PhosphorIconName.CircleHalfIcon]: CircleHalf,
  [PhosphorIconName.ClockCounterClockwiseIcon]: ClockCounterClockwise,
  [PhosphorIconName.CrosshairIcon]: Crosshair,
  [PhosphorIconName.CopyIcon]: Copy,
  [PhosphorIconName.EqualsIcon]: Equals,
  [PhosphorIconName.EyeIcon]: Eye,
  [PhosphorIconName.EyeSlashIcon]: EyeSlash,
  [PhosphorIconName.FileCsvIcon]: FileCsv,
  [PhosphorIconName.FileXlsIcon]: FileXls,
  [PhosphorIconName.FlagIcon]: Flag,
  [PhosphorIconName.FunnelSimpleIcon]: FunnelSimpleIcon,
  [PhosphorIconName.GitCommitIcon]: GitCommit,
  [PhosphorIconName.GlobeHemisphereWestIcon]: GlobeHemisphereWest,
  [PhosphorIconName.GreaterThanIcon]: GreaterThan,
  [PhosphorIconName.GreaterThanOrEqualIcon]: GreaterThanOrEqual,
  [PhosphorIconName.LessThanIcon]: LessThan,
  [PhosphorIconName.LessThanOrEqualIcon]: LessThanOrEqual,
  [PhosphorIconName.MemberOfIcon]: MemberOf,
  [PhosphorIconName.NoteIcon]: Note,
  [PhosphorIconName.NotEqualsIcon]: NotEquals,
  [PhosphorIconName.NotMemberOfIcon]: NotMemberOf,
  [PhosphorIconName.PlusMinusIcon]: PlusMinus,
  [PhosphorIconName.ProhibitIcon]: Prohibit,
  [PhosphorIconName.PushPinSimpleIcon]: PushPinSimpleIcon,
  [PhosphorIconName.SealPercentIcon]: SealPercent,
  [PhosphorIconName.SidebarSimpleIcon]: SidebarSimple,
  [PhosphorIconName.TargetIcon]: Target,
  [PhosphorIconName.TextAUnderlineIcon]: TextAUnderline,
}

export function PhosphorIcon({
  iconName,
  weight,
  ...props
}: PhosphorIconProps) {
  const IconComponent = iconMap[iconName as PhosphorIconName]

  if (!IconComponent) {
    throw new Error(`Phosphor icon "${iconName}" not found`)
  }

  return (
    <IconBase
      IconComponent={IconComponent}
      iconName={iconName}
      {...props}
      type="phosphor"
      weight={weight}
    />
  )
}
